import React, { useEffect, useState, useRef, useCallback } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Skeleton, Card, Button, Input, Spin, message } from 'antd';
import { Product, SupplierProductsData } from 'models/SupplierDynamics';
import { useProductIQApi } from 'api/product_iq';
import CompareProductsModal from './CompareProductsModal';
import { CompareFooter } from '../compare-footer/compare-footer';

interface ProductsListProps {
  vendorDomain: string;
  addToSelectedProducts: (product: Product) => void;
  productList: Product[];
  productSearchText?: string;
  loadingProducts?: boolean;
  hasMoreProducts: boolean;
  setHasMoreProducts: (hasMoreProducts: boolean) => void;
}

/**
 * ProductsList component displays a grid of product cards with search functionality.
 * Each card shows product name, description, and key features.
 * @param {ProductsListProps} props - Component props
 * @param {string} props.vendorDomain - The vendor domain to fetch products for
 * @param {Product[]} props.products - Array of products to display
 * @returns {React.ReactElement} The rendered ProductsList component
 */
export const ProductsList: React.FC<ProductsListProps> = ({
  vendorDomain,
  addToSelectedProducts,
  productList,
  hasMoreProducts,
  setHasMoreProducts,
  productSearchText = '',
  loadingProducts = false
}): React.ReactElement => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchText, setSearchText] = useState<string>(productSearchText);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(2);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const { fetchProductIQProducts } = useProductIQApi();

  /**
   * Loads more products when user scrolls to bottom
   */
  const loadMoreProducts = useCallback(async () => {
    if (!vendorDomain || loadingMore || !hasMoreProducts) {
      return;
    }

    setLoadingMore(true);
    try {
      if (products.length === 50) {
        const pageSize = 10;
        const response: SupplierProductsData | undefined = await fetchProductIQProducts(
          vendorDomain,
          currentPage,
          pageSize
        );
        if (response?.content) {
          const newProducts = response.content.map((item: any) => ({
            name: item.name,
            overview: item.overview,
            category: item.category,
            features: item.features || [],
            vendor_domain: vendorDomain
          }));
          setProducts((prevProducts) => [...prevProducts, ...newProducts]);
          // Check if there are more products to load
          // If the response has fewer items than requested, it means we've reached the last page
          if (response.pagination?.last || response.content.length < pageSize) {
            setHasMoreProducts(false);
          }
        }
      }
    } catch (error) {
      message.error('Error loading more products');
    } finally {
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorDomain, currentPage, loadingMore, hasMoreProducts]);

  useEffect(() => {
    if (vendorDomain) {
      // Initialize products with the initial load from parent
      setProducts(productList);
      setHasInitiallyLoaded(!loadingProducts);
      // Reset pagination state for new vendor (start at page 2 for next load)
      setCurrentPage(2);
      setHasMoreProducts(true);
      setLoadingMore(false);
    } else {
      setProducts([]);
      setSearchText('');
      setCurrentPage(2);
      setHasMoreProducts(true);
      setLoadingMore(false);
      setHasInitiallyLoaded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorDomain, JSON.stringify(productList), loadingProducts]);

  /**
   * Mark initial load complete when loading finishes
   */
  useEffect(() => {
    if (!loadingProducts && vendorDomain) {
      setHasInitiallyLoaded(true);
    }
  }, [loadingProducts, vendorDomain]);

  /**
   * Sync searchText when productSearchText prop changes
   */
  useEffect(() => {
    setSearchText(productSearchText);
  }, [productSearchText]);

  /**
   * Setup intersection observer for infinite scroll
   */
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && hasMoreProducts && !loadingMore && products.length > 0) {
          loadMoreProducts();
        }
      });
    }, options);

    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }

    return () => {
      if (observerRef.current && sentinelRef.current) {
        observerRef.current.unobserve(sentinelRef.current);
      }
    };
  }, [hasMoreProducts, loadingMore, products.length, loadMoreProducts]);

  /**
   * Filters products based on search text
   */
  const filteredProducts = React.useMemo(() => {
    if (!searchText || searchText.trim() === '') {
      return products;
    }

    // Otherwise, filter products based on searchText
    return products.filter(
      (product) =>
        product.name.toLowerCase().includes(searchText.toLowerCase()) ||
        product.overview?.toLowerCase().includes(searchText.toLowerCase()) ||
        product.features.some((feature) => feature.toLowerCase().includes(searchText.toLowerCase())) ||
        product.category?.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [products, searchText]);

  /**
   * Handles search input change
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
   */
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  return (
    <div className="w-full p-6">
      {/* Header Section */}

      {/* Products Grid */}
      {loadingProducts ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, index) => (
            <Card key={`skeleton-${index}`} className="max-h-96">
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map((product) => (
              <Card
                key={product?.name}
                className={`max-h-96 flex flex-col hover:shadow-lg transition-shadow shadow-none duration-200 overflow-hidden ${
                  searchText && searchText.trim() !== '' ? 'border-blue-500' : 'hover:border-blue-500'
                }`}
                bodyStyle={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '24px',
                  overflowY: 'auto'
                }}
                title={
                  <div className="flex items-start justify-between">
                    <span className="text-xl font-semibold text-gray-900 flex-1">{product.name}</span>
                  </div>
                }
                actions={[
                  <Button
                    key={product?.name}
                    type="link"
                    icon={<SearchOutlined />}
                    onClick={() => {
                      addToSelectedProducts(product);
                    }}
                  >
                    Find Competitors and Compare Features
                  </Button>
                ]}
              >
                {/* Product Overview */}
                <p className="text-gray-600 text-sm mb-4">{product.overview}</p>

                {/* Key Features */}
                <div className="flex-1 mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {product.features.length > 0 ? (
                      product.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-blue-500 mr-2">•</span>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-sm text-gray-500 italic">No features available for this product.</li>
                    )}
                  </ul>
                </div>
              </Card>
            ))}
          </div>

          {/* Sentinel for infinite scroll trigger */}
          <div ref={sentinelRef} className="mt-8 flex justify-center" />

          {/* Loading indicator for more products */}
          {loadingMore && (
            <div className="flex justify-center items-center py-8 gap-2">
              <Spin size="large" />
              <span className="text-gray-600">Loading more products...</span>
            </div>
          )}
        </>
      )}

      {/* Empty State */}
      {!loadingProducts && filteredProducts.length === 0 && hasInitiallyLoaded && (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-2">No products found</div>
          <div className="text-gray-400 text-sm">
            {searchText ? 'Try adjusting your search criteria' : 'No products available for this vendor'}
          </div>
        </div>
      )}
    </div>
  );
};
